import {
  BasketballPlayer,
  BasketballTeam,
  BasketballTeamSettings,
  SelectOption,
} from '@hefe/models';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { BasketballPlayerEditRequest } from '../../../models/requests/basketball-player';
import {
  BasketballTeamAddPlayersRequest,
  BasketballTeamAddTeamRequest,
  BasketballTeamRemovePlayersRequest,
  BasketballTeamsSyncPracticesUntilRequest,
  BasketballTeamTransferPlayersRequest,
  BasketballTeamTransferPlayersResponse,
  BasketballTeamUpdateTeamRequest,
} from '../../../models/requests/basketball-team';
import { isCacheValid } from '../../../utils/cache/caching';
import { UserState } from '../../base/user/user.slice';

const FEATURE_KEY = 'basketballTeams';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */

export const fetchBasketballTeam = createAsyncThunk(
  'basketballTeams/get',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballTeams().get(teamId);
    return response?.data.data;
  }
);

export const fetchBasketballTeams = createAsyncThunk(
  'basketballTeams/getAll',
  async (thunkAPI) => {
    const response = await API.basketballTeams().getAll();
    return response?.data.data;
  }
);

export const fetchBasketballTeamsSelect = createAsyncThunk(
  'basketballTeams/getAllSelect',
  async (data: { cacheDuration: number }, thunkAPI) => {
    if (!isCacheValid('basketballTeams/getAllSelect', data, thunkAPI)) {
      const response = await API.basketballTeams().getAllSelect();
      return response?.data;
    }
  }
);

export const createBasketballTeam = createAsyncThunk(
  'basketballTeams/create',
  async (data: BasketballTeamAddTeamRequest, thunkAPI) => {
    const response = await API.basketballTeams().create(data);
    return response?.data;
  }
);

export const updateBasketballTeam = createAsyncThunk(
  'basketballTeams/update',
  async (data: BasketballTeamUpdateTeamRequest, thunkAPI) => {
    const response = await API.basketballTeams().update(data);
    return response?.data;
  }
);

export const deleteBasketballTeam = createAsyncThunk<any, BasketballTeam>(
  'basketballTeams/delete',
  async (request: BasketballTeam, thunkAPI) => {
    const response = await API.basketballTeams().delete(request);
    if (response) {
      return thunkAPI.fulfillWithValue(request);
    } else {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

export const updateBasketballTeamSettings = createAsyncThunk(
  'basketballTeams/updateSettings',
  async (
    data: {
      teamId: number;
      settings: { settings: BasketballTeamSettings };
    },
    thunkAPI
  ) => {
    const response = await API.basketballTeams().updateSettings(
      data.teamId,
      data.settings
    );
    return response?.data;
  }
);

export const updateBasketballTeamsSyncPracticesUntil = createAsyncThunk(
  'basketballTeams/syncPracticesUntil',
  async (data: BasketballTeamsSyncPracticesUntilRequest, thunkAPI) => {
    const response = await API.basketballTeams().syncPracticesUntil(data);
    return response?.data;
  }
);

/************************************************************************************
 *                              PLAYERS ACTIONS
 *************************************************************************************/
export const fetchBasketballTeamPlayers = createAsyncThunk(
  'basketballTeams/getPlayers',
  async (teamId: number, thunkAPI) => {
    const response = await API.basketballTeams().getPlayers(teamId);

    const userState = (thunkAPI.getState() as any).user as UserState;
    const activeCoachTeamId =
      userState.user?.basketball_user?.active_coach_team_id;

    return {
      isActiveCoachTeam: teamId === activeCoachTeamId,
      teamId: teamId,
      players: response?.data,
    };
  }
);

export const addPlayersToBasketballTeam = createAsyncThunk(
  'basketballTeams/addPlayers',
  async (data: BasketballTeamAddPlayersRequest, thunkAPI) => {
    const response = await API.basketballTeams().addPlayers(data);

    const userState = (thunkAPI.getState() as any).user as UserState;
    const activeCoachTeamId =
      userState.user?.basketball_user?.active_coach_team_id;

    return {
      isActiveCoachTeam: data.team_id === activeCoachTeamId,
      teamId: data.team_id,
      players: response?.data,
    };
  }
);

export const removePlayersFromBasketballTeam = createAsyncThunk(
  'basketballTeams/removePlayers',
  async (data: BasketballTeamRemovePlayersRequest, thunkAPI) => {
    const response = await API.basketballTeams().removePlayers(data);

    const userState = (thunkAPI.getState() as any).user as UserState;
    const activeCoachTeamId =
      userState.user?.basketball_user?.active_coach_team_id;

    return {
      isActiveCoachTeam: data.team_id === activeCoachTeamId,
      teamId: data.team_id,
      players: response?.data,
    };
  }
);

export const transferPlayersFromBasketballTeam = createAsyncThunk(
  'basketballTeams/transferPlayers',
  async (data: BasketballTeamTransferPlayersRequest, thunkAPI) => {
    const response = await API.basketballTeams().transferPlayers(data);

    return response?.data as BasketballTeamTransferPlayersResponse;
  }
);

export const updateBasketballPlayer = createAsyncThunk(
  'basketballTeams/editPlayer',
  async (data: BasketballPlayerEditRequest, thunkAPI) => {
    const response = await API.basketballPlayers().update(data);

    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballTeamsState {
  teams: BasketballTeam[];
  teamsSelect: SelectOption[];
}

const initialState: BasketballTeamsState = {
  teams: [],
  teamsSelect: [],
};

export const basketballTeamsSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    setTeamSyncUntil: (
      state: BasketballTeamsState,
      action: PayloadAction<{ teamId: number; syncUntil: string }>
    ) => {
      const team = state.teams.find((t) => t.id === action.payload.teamId);

      if (team) {
        team.sync_practices_until = action.payload.syncUntil;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballTeam.fulfilled,
      (state: BasketballTeamsState, action: PayloadAction<BasketballTeam>) => {
        const team = state.teams.find((t) => t.id === action.payload.id);
        if (team) {
          state.teams.map((t) =>
            t.id === action.payload.id ? action.payload : t
          );
        } else {
          state.teams.push(action.payload);
        }
      }
    );
    builder.addCase(
      fetchBasketballTeams.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<BasketballTeam[]>
      ) => {
        state.teams = action.payload;
      }
    );
    builder.addCase(
      fetchBasketballTeamsSelect.fulfilled,
      (state: BasketballTeamsState, action: PayloadAction<SelectOption[]>) => {
        if (action.payload) {
          state.teamsSelect = action.payload;
        }
      }
    );
    builder.addCase(
      createBasketballTeam.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<{ data: BasketballTeam }>
      ) => {
        state.teams.push(action.payload.data);
        state.teams.sort((a: BasketballTeam, b: BasketballTeam) =>
          a.name.localeCompare(b.name)
        );
      }
    );
    builder.addCase(
      updateBasketballTeam.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<{ data: BasketballTeam }>
      ) => {
        state.teams = state.teams.map((team) =>
          team.id === action.payload.data.id ? action.payload.data : team
        );
      }
    );
    builder.addCase(
      deleteBasketballTeam.fulfilled,
      (state: BasketballTeamsState, action: PayloadAction<BasketballTeam>) => {
        state.teams = state.teams.filter(
          (team) => team.id !== action.payload.id
        );
      }
    );
    builder.addCase(
      updateBasketballTeamSettings.fulfilled,
      (state: BasketballTeamsState, action: PayloadAction<BasketballTeam>) => {
        const team = state.teams.find((t) => t.id === action.payload.id);
        if (team) {
          team.settings = JSON.parse(
            action.payload.settings as unknown as string
          );
        }
      }
    );
    builder.addCase(
      updateBasketballTeamsSyncPracticesUntil.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<{ data: BasketballTeam[] }>
      ) => {
        state.teams = action.payload.data;
      }
    );

    /************************************************************************************
     *                              PLAYERS REDUCERS
     *************************************************************************************/
    builder.addCase(
      transferPlayersFromBasketballTeam.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<BasketballTeamTransferPlayersResponse>
      ) => {
        const fromTeam = state.teams.find(
          (team) => team.id === action.payload.team_from.id
        );
        const toTeam = state.teams.find(
          (team) => team.id === action.payload.team_to.id
        );
        if (fromTeam) {
          fromTeam.players = action.payload.team_from.players;
        }
        if (toTeam) {
          toTeam.players = action.payload.team_to.players;
        }
      }
    );
    builder.addCase(
      updateBasketballPlayer.fulfilled,
      (
        state: BasketballTeamsState,
        action: PayloadAction<{ data: BasketballPlayer }>
      ) => {
        const team = state.teams.find(
          (team) => team.id === action.payload.data.team_id
        );

        if (team) {
          team.players = team.players?.map((player) =>
            player.id === action.payload.data.id ? action.payload.data : player
          );
        }
      }
    );
    builder.addMatcher(
      isAnyOf(
        fetchBasketballTeamPlayers.fulfilled,
        addPlayersToBasketballTeam.fulfilled,
        removePlayersFromBasketballTeam.fulfilled
      ),
      (
        state: BasketballTeamsState,
        action: PayloadAction<{
          isActiveCoachTeam: boolean;
          teamId: number;
          players: BasketballPlayer[];
        }>
      ) => {
        const team = state.teams.find(
          (team) => team.id === action.payload.teamId
        );
        if (team) {
          team.players = action.payload.players;
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballTeamsReducer = basketballTeamsSlice.reducer;
export const basketballTeamsActions = basketballTeamsSlice.actions;

const state = (rootState: any): BasketballTeamsState => rootState[FEATURE_KEY];

export const basketballTeamsSelector = createSelector(state, (state) => {
  return state.teams;
});

export const basketballTeamSelector = (id: number) => (state: any) => {
  const teamsState = state[FEATURE_KEY] as BasketballTeamsState;

  return teamsState.teams.find((team) => team.id === id);
};

export const basketballTeamsSelectSelector = createSelector(state, (state) => {
  return state.teamsSelect;
});

export const basketballTeamsSelectWithoutPendingSelector = createSelector(
  state,
  (state) => {
    return state.teamsSelect.filter((team) => team.value !== null);
  }
);

export const basketballTeamsSelectWithoutPendingWithPrefixSelector =
  createSelector(state, (state) => {
    return state.teamsSelect
      .filter((team) => team.value !== null)
      .map((team) => ({
        ...team,
        value: 't_' + team.value,
      }));
  });
